/**
 * LibraryModal.tsx — the Linear-style file picker (File → Library).
 *
 * This is a PICKER, not a manager: open a file from the library into the
 * current session. The full library manager is the start page (Composer).
 *
 * UX:
 *   ┌───────────────────────────────────────────────┐
 *   │ [🔍 Search library…                    ] [✕]   │  ← header IS the search
 *   ├───────────────────────────────────────────────┤
 *   │ ▸ Food Delivery          6 actors · 2d ago     │
 *   │   Customers → CRM → kitchen → delivery…        │
 *   │ ▸ Food Delivery — Burst  6 actors · 2d ago     │
 *   │   …                                           │
 *   └───────────────────────────────────────────────┘
 *
 * - Search input is the header, autofocus, ✕ to clear, spinner while loading.
 * - Real list rows with metadata: label, description, tags, `N actors · <relative>`.
 * - Keyboard: ↑/↓ move selection, Enter open, Esc close, typing filters live.
 *
 * The adapter is injected (LibraryAdapter) so the picker is source-agnostic:
 * the same component opens from local storage or a remote /api/library.
 */
import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, SpinnerGap, Tag, X, FileText } from '@phosphor-icons/react';
import type { LibraryAdapter, LibraryEntry } from '../library/LibraryAdapter';
import { useLibrary } from '../library/useLibrary';

interface LibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  adapter: LibraryAdapter;
  onSelect: (entry: LibraryEntry) => void;
}

export function LibraryModal({ isOpen, onClose, adapter, onSelect }: LibraryModalProps): JSX.Element | null {
  const { entries, loading, query, setQuery } = useLibrary(adapter);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset selection when results change; refocus the input on open.
  useEffect(() => {
    setActive(0);
  }, [entries, isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Keep the active row scrolled into view during keyboard nav.
  useEffect(() => {
    if (!isOpen) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [active, isOpen]);

  if (!isOpen) return null;

  const clamp = (i: number) => Math.max(0, Math.min(i, Math.max(0, entries.length - 1)));
  const choose = (entry: LibraryEntry) => {
    onSelect(entry);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => clamp(i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => clamp(i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = entries[active];
      if (entry) choose(entry);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="picker"
        role="dialog"
        aria-modal="true"
        aria-label="Open from library"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <header className="picker-header">
          <MagnifyingGlass size={16} className="picker-search-icon" />
          <input
            ref={inputRef}
            className="picker-input"
            type="text"
            placeholder="Search library…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {loading ? (
            <SpinnerGap size={16} className="picker-spinner" />
          ) : query ? (
            <button
              type="button"
              className="picker-clear"
              aria-label="Clear search"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
            >
              <X size={14} />
            </button>
          ) : null}
          <button type="button" className="picker-close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="picker-body" ref={listRef}>
          <PickerContent
            entries={entries}
            loading={loading}
            active={active}
            query={query}
            onHover={setActive}
            onSelect={choose}
          />
        </div>

        <footer className="picker-footer">
          <span className="hint">
            <kbd>↑</kbd> <kbd>↓</kbd> navigate · <kbd>↵</kbd> open · <kbd>Esc</kbd> close
          </span>
          <span className="picker-count">{entries.length} item{entries.length === 1 ? '' : 's'}</span>
        </footer>
      </div>
    </div>
  );
}

/** Body states: loading skeletons, empty library, no-results, or the list. */
function PickerContent({
  entries,
  loading,
  active,
  query,
  onHover,
  onSelect,
}: {
  entries: LibraryEntry[];
  loading: boolean;
  active: number;
  query: string;
  onHover: (i: number) => void;
  onSelect: (entry: LibraryEntry) => void;
}) {
  if (loading && entries.length === 0) {
    return (
      <div className="picker-skeletons">
        {Array.from({ length: 5 }).map((_, i) => (
          <div className="picker-skeleton" key={i} />
        ))}
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="picker-empty">
        {query ? (
          <>
            <div className="picker-empty-title">No matches</div>
            <div className="picker-empty-sub">Nothing in the library matches “{query}”.</div>
          </>
        ) : (
          <>
            <div className="picker-empty-title">Library is empty</div>
            <div className="picker-empty-sub">Save a file from File → Save to add it here.</div>
          </>
        )}
      </div>
    );
  }
  return (
    <ul className="picker-list" role="listbox">
      {entries.map((entry, i) => (
        <li
          key={entry.id}
          data-idx={i}
          role="option"
          aria-selected={i === active}
          className={`picker-row${i === active ? ' selected' : ''}`}
          onMouseEnter={() => onHover(i)}
          onClick={() => onSelect(entry)}
        >
          <span className="picker-row-icon">
            <FileText size={16} />
          </span>
          <span className="picker-row-main">
            <span className="picker-row-title">{entry.label}</span>
            <span className="picker-row-desc">{entry.description || 'No description'}</span>
            {entry.tags.length > 0 && (
              <span className="picker-row-tags">
                {entry.tags.map((t) => (
                  <span key={t} className="tag-chip static"><Tag size={9} /> {t}</span>
                ))}
              </span>
            )}
          </span>
          <span className="picker-row-meta">
            <RowMeta entry={entry} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** `N actors · updated <relative>`. */
function RowMeta({ entry }: { entry: LibraryEntry }) {
  const parts: string[] = [];
  if (entry.nodeCount > 0) parts.push(`${entry.nodeCount} actor${entry.nodeCount === 1 ? '' : 's'}`);
  if (entry.updatedAt > 0) parts.push(relativeTime(entry.updatedAt));
  return <>{parts.join(' · ') || '—'}</>;
}

/** Compact relative-time formatter (ms epoch → "2d ago"). */
function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const sec = Math.round(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}
